import { logger } from '@/sentry.config'
import { OrderReportResponse } from '@/types/OrderReportTypes'
import { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { getPatientInfoFromForm } from '@/utils/orderFormUtils'
import { FileLines } from 'flowbite-react-icons/outline'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getAllOrderReports, getOrderReportById } from '../../features/report/services/reportClient'
import PDFViewer from '../PDFViewer'

interface OrderReportProps {
  orderId?: string
  orderData?: OrderFormInfoData
}

/**
 * OrderReport Component
 *
 * Displays the lab test results PDF inline within the order confirmation page.
 * This component is shown when the order status is "Result Ready".
 * Supports multiple reports with selection capability.
 */
const OrderReport: React.FC<OrderReportProps> = ({ orderId, orderData }) => {
  const [reports, setReports] = useState<OrderReportResponse[]>([])
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null)
  const [labResultPdfBytes, setLabResultPdfBytes] = useState<Uint8Array | null>(null)
  const [isLoadingReports, setIsLoadingReports] = useState(true)
  const [isLoadingPDF, setIsLoadingPDF] = useState(false)
  const [reportsError, setReportsError] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const patientName = getPatientInfoFromForm(orderData ?? null).name || null

  // Compute caller from ordering provider name
  const caller = useMemo(() => {
    if (orderData?.orderingProvider?.name) {
      const firstName = orderData.orderingProvider.name.firstName || ''
      const lastName = orderData.orderingProvider.name.lastName || ''
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || 'spa'
    }
    return 'spa'
  }, [orderData?.orderingProvider?.name])

  // Find the latest report based on generated_at
  const latestReport = useMemo(() => {
    if (reports.length === 0) return null

    return reports.reduce((latest, report) => {
      const latestDate = latest.generated_at ? new Date(latest.generated_at).getTime() : 0
      const reportDate = report.generated_at ? new Date(report.generated_at).getTime() : 0

      // Handle invalid dates (NaN) - prefer valid dates over invalid ones
      const latestIsValid = !isNaN(latestDate) && latestDate > 0
      const reportIsValid = !isNaN(reportDate) && reportDate > 0

      // If current report has invalid date and latest has valid date, keep latest
      if (!reportIsValid && latestIsValid) return latest
      // If latest has invalid date and current report has valid date, use current report
      if (reportIsValid && !latestIsValid) return report
      // If both are invalid or both are valid, compare normally
      return reportDate > latestDate ? report : latest
    })
  }, [reports])

  // Fetch all reports for the order
  // Use a ref to track the current request and prevent race conditions
  const currentReportsRequestRef = useRef<string | null>(null)

  useEffect(() => {
    if (!orderId) {
      // If orderId is missing, stop loading and show appropriate state
      setIsLoadingReports(false)
      setReportsError('Order ID is required to fetch reports')
      currentReportsRequestRef.current = null
      return
    }

    // Create a unique request ID for this fetch
    const requestId = `${orderId}-${caller}`
    currentReportsRequestRef.current = requestId

    const fetchAllReports = async () => {
      try {
        setIsLoadingReports(true)
        setReportsError(null)

        const allReports = await getAllOrderReports(orderId, caller)

        // Only update state if this request is still the current one (not stale)
        if (currentReportsRequestRef.current === requestId) {
          setReports(allReports)

          // Auto-select latest report if available
          if (allReports.length > 0) {
            const latest = allReports.reduce((latest, report) => {
              const latestDate = latest.generated_at ? new Date(latest.generated_at).getTime() : 0
              const reportDate = report.generated_at ? new Date(report.generated_at).getTime() : 0

              // Handle invalid dates (NaN) - prefer valid dates over invalid ones
              const latestIsValid = !isNaN(latestDate) && latestDate > 0
              const reportIsValid = !isNaN(reportDate) && reportDate > 0

              // If current report has invalid date and latest has valid date, keep latest
              if (!reportIsValid && latestIsValid) return latest
              // If latest has invalid date and current report has valid date, use current report
              if (reportIsValid && !latestIsValid) return report
              // If both are invalid or both are valid, compare normally
              return reportDate > latestDate ? report : latest
            })
            setSelectedReportId(latest.id)
          }
        }
      } catch (error) {
        // Only update state if this request is still the current one (not stale)
        if (currentReportsRequestRef.current === requestId) {
          logger.error('Failed to fetch reports', {
            orderId,
            error: error instanceof Error ? error.message : String(error),
          })
          setReportsError(error instanceof Error ? error.message : 'Failed to fetch reports')
        }
      } finally {
        // Only update loading state if this request is still the current one (not stale)
        if (currentReportsRequestRef.current === requestId) {
          setIsLoadingReports(false)
        }
      }
    }

    fetchAllReports()

    // Cleanup function to mark this request as stale when dependencies change
    return () => {
      currentReportsRequestRef.current = null
    }
  }, [orderId, caller])

  // Fetch PDF for selected report
  // Use a ref to track the current request and prevent race conditions
  const currentRequestRef = useRef<string | null>(null)

  useEffect(() => {
    if (!orderId || !selectedReportId) {
      setLabResultPdfBytes(null)
      setIsLoadingPDF(false)
      return
    }

    // Create a unique request ID for this fetch
    const requestId = `${orderId}-${selectedReportId}-${caller}`
    currentRequestRef.current = requestId

    const fetchLabResultPDF = async () => {
      try {
        setIsLoadingPDF(true)
        setPdfError(null)

        const pdfBytes = await getOrderReportById(orderId, selectedReportId, caller)

        // Only update state if this request is still the current one (not stale)
        if (currentRequestRef.current === requestId) {
          setLabResultPdfBytes(pdfBytes)
        }
      } catch (error) {
        // Only update state if this request is still the current one (not stale)
        if (currentRequestRef.current === requestId) {
          logger.error('Failed to fetch lab result PDF', {
            orderId,
            reportId: selectedReportId,
            error: error instanceof Error ? error.message : String(error),
          })
          setPdfError(error instanceof Error ? error.message : 'Failed to fetch lab results PDF')
          setLabResultPdfBytes(null)
        }
      } finally {
        // Only update loading state if this request is still the current one (not stale)
        if (currentRequestRef.current === requestId) {
          setIsLoadingPDF(false)
        }
      }
    }

    fetchLabResultPDF()

    // Cleanup function to mark this request as stale when dependencies change
    return () => {
      currentRequestRef.current = null
    }
  }, [orderId, selectedReportId, caller])

  // Get selected report details
  const selectedReport = useMemo(() => {
    return reports.find((r) => r.id === selectedReportId) || null
  }, [reports, selectedReportId])

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      // Check if the date is valid - invalid dates have NaN for getTime()
      if (isNaN(date.getTime())) {
        return 'N/A'
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return 'N/A'
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Report Information Header */}
      <div className="mb-4 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
        <h3 className="mb-3 self-stretch text-sm leading-5 font-medium tracking-tight text-text-heading dark:text-white">Report Information</h3>
        <dl className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <dt className="text-sm leading-5 font-medium text-text-body dark:text-gray-400">Order ID</dt>
            <dd className="mt-1 font-mono text-sm leading-5 font-medium text-text-heading dark:text-white">{orderId || 'N/A'}</dd>
          </div>
          <div>
            <dt className="text-sm leading-5 font-medium text-text-body dark:text-gray-400">Patient Name</dt>
            <dd className="mt-1 text-sm leading-5 font-medium text-text-heading dark:text-white">{patientName || 'N/A'}</dd>
          </div>
          <div>
            <dt className="text-sm leading-5 font-medium text-text-body dark:text-gray-400">Results Date</dt>
            <dd className="mt-1 text-sm leading-5 font-medium text-text-heading dark:text-white">{formatDate(selectedReport?.generated_at)}</dd>
          </div>
          <div>
            <dt className="text-sm leading-5 font-medium text-text-body dark:text-gray-400">Status</dt>
            <dd className="mt-1">
              <span className="text-xs leading-4 font-medium inline-flex items-center rounded-full bg-success-100 dark:bg-gray-900 px-2.5 py-0.5 text-text-fg-success-strong dark:text-success-100">
                Complete
              </span>
            </dd>
          </div>
        </dl>

        {/* Reports List */}
        {isLoadingReports ? (
          <div className="flex items-center justify-center py-4">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-border-success dark:border-success-700 border-r-transparent dark:border-r-transparent"></div>
            <p className="ml-2 text-sm leading-5 font-medium text-text-heading dark:text-white">Loading reports...</p>
          </div>
        ) : reportsError ? (
          <div className="rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-3">
            <p className="text-sm leading-5 font-medium text-text-heading dark:text-white">{reportsError}</p>
          </div>
        ) : reports.length > 0 ? (
          <div className="mt-4">
            <h4 className="text-sm leading-5 font-medium text-text-body dark:text-gray-400">Available Reports</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border-base dark:divide-gray-700">
                <thead className="bg-background-secondary-medium dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-body dark:text-gray-400">
                      Report Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-body dark:text-gray-400">
                      Version
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-body dark:text-gray-400">
                      Generated At
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-base dark:divide-gray-700 bg-white dark:bg-gray-900">
                  {reports.map((report) => {
                    const isSelected = report.id === selectedReportId
                    const isLatest = report.id === latestReport?.id
                    return (
                      <tr
                        key={report.id}
                        onClick={() => setSelectedReportId(report.id)}
                        className={`cursor-pointer transition-colors ${
                          isSelected ? 'bg-success-50 dark:bg-gray-800 hover:bg-success-100 dark:hover:bg-gray-700' : 'hover:bg-background-secondary-medium dark:hover:bg-gray-800'
                        }`}
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center">
                            <span className="text-sm leading-5 font-medium text-text-heading dark:text-white">{report.report_type || 'N/A'}</span>
                            {isLatest && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-success-100 dark:bg-gray-900 px-2 py-0.5 text-xs font-medium text-text-fg-success-strong dark:text-success-100">
                                Latest
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="text-sm leading-5 font-medium text-text-heading dark:text-white">
                            {report.version !== null && report.version !== undefined ? String(report.version) : 'N/A'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="text-sm leading-5 font-medium text-text-heading dark:text-white">{formatDate(report.generated_at)}</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-base border border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 p-4">
            <p className="text-sm leading-5 font-medium text-text-heading dark:text-white">No reports available for this order.</p>
          </div>
        )}
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 overflow-hidden rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900">
        {isLoadingPDF ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-border-success dark:border-success-700 border-r-transparent dark:border-r-transparent"></div>
              <p className="mt-2 text-sm leading-5 font-medium text-text-heading dark:text-white">Loading results PDF...</p>
            </div>
          </div>
        ) : pdfError ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center">
              <FileLines className="mx-auto h-12 w-12 text-text-subtle dark:text-gray-500" />
              <p className="mt-4 text-sm leading-5 font-medium text-text-heading dark:text-white">Unable to load results PDF</p>
              <p className="mt-2 text-xs leading-4 text-text-subtle dark:text-gray-500">{pdfError}</p>
            </div>
          </div>
        ) : labResultPdfBytes ? (
          <PDFViewer pdfBytes={labResultPdfBytes} filename="Lab-Results.pdf" className="h-full" />
        ) : selectedReportId ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center">
              <FileLines className="mx-auto h-12 w-12 text-text-subtle dark:text-gray-500" />
              <p className="mt-4 text-sm leading-5 font-medium text-text-heading dark:text-white">No PDF available for selected report</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default OrderReport
